import { Box, Button, Paper, Stack, Switch, Typography, useTheme } from "@mui/material";
import { FeatureItem } from "../feature.types";
import { FoodsIcon } from "@foodpilot/foods";

type Props = {
    feature: FeatureItem;
    index: number;
};
export const GeneralFeature = (props: Props) => {
    const theme = useTheme();

    const { feature, index } = props;
    return (
        <Paper
            key={index}
            elevation={0}
            sx={{
                border: "1px solid #E7E6E4",
                borderRadius: "8px",
                backgroundColor: "white",
                padding: "24px 16px",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={2}>
                {/* Feature icon */}
                <FoodsIcon icon={getFeatureIcon(feature.name, feature.appParamKey) as any} size={2} />

                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                    <Typography
                        variant="body"
                        sx={{
                            marginBottom: "4px",
                        }}
                    >
                        {feature.name}
                    </Typography>
                    <Typography
                        variant="small"
                        sx={{
                            color: "#767673",
                        }}
                    >
                        {feature.description}
                    </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={2}>
                    {feature.configureButton && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!feature.enabled}
                            onClick={feature.configureButton.onClick}
                            sx={{
                                textTransform: "none",
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                borderColor: theme.palette.primary[600],
                                color: theme.palette.primary[600],
                                "&:hover": {
                                    borderColor: theme.palette.primary[700],
                                    backgroundColor: theme.palette.primary[50],
                                },
                                "&:disabled": {
                                    opacity: 0.36,
                                    borderColor: theme.custom.grey[500],
                                    color: theme.custom.grey[2800],
                                },
                            }}
                        >
                            {feature.configureButton.text}
                        </Button>
                    )}
                    <Switch
                        checked={feature.enabled}
                        onChange={(e) => feature.onToggle?.(e.target.checked)}
                        sx={{
                            width: 44,
                            height: 24,
                            padding: 0,
                            "& .MuiSwitch-switchBase": {
                                padding: 0,
                                margin: "4px",
                                transitionDuration: "300ms",
                                "&.Mui-checked": {
                                    transform: "translateX(20px)",
                                    color: "#fff",
                                    "& + .MuiSwitch-track": {
                                        backgroundColor: theme.palette.primary[600],
                                        opacity: 1,
                                        border: 0,
                                    },
                                },
                            },
                            "& .MuiSwitch-thumb": {
                                boxSizing: "border-box",
                                width: 16,
                                height: 16,
                            },
                            "& .MuiSwitch-track": {
                                borderRadius: 20,
                                backgroundColor: "#D8D7D5",
                                opacity: 1,
                                transition: "background-color 0.5s",
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Paper>
    );
};

// Helper function to get feature icon based on name or appParamKey
function getFeatureIcon(featureName: string, appParamKey?: string): string {
    // Use appParamKey first for reliable identification
    if (appParamKey) {
        switch (appParamKey) {
            case "FS-cms-enabled":
                return "home";
            case "FS-entity-companies":
                return "corporate";
            case "FS-entity-products":
                return "ingredients";
            case "FS-entity-intermediate-products":
                return "flour";
            case "FS-manage-my-ingredients":
                return "wheat";
            case "FS-manage-my-packagings":
                return "packaging";
            case "FS-strategy":
                return "strategy";
            case "FS-documentary-space":
                return "documentation";
            case "FS-news-feed":
                return "forum";
            case "FS-icv-management":
                return "repositories";
            case "FS-actionPlan":
                return "trajectory";
        }
    }

    // Fallback to name matching
    switch (featureName) {
        case "CMS":
            return "home";
        case "Page stratégie":
            return "strategy";
        case "Documentary Space Page":
            return "documentation";
        case "Company Settings":
            return "transformationSteps";
        case "Payment":
            return "reference";
        case "Forum":
            return "forum";
        case "Diagnostic":
            return "diagnostic";
        case "Page Action Plan":
        case "Action Plan":
        case "Trajectory / Action plan":
            return "trajectory";
        case "Newsfeed":
        case "Fil d'actualité":
            return "forum";
        case "Gestion des ICVs":
            return "repositories";
        default:
            return "default";
    }
}
